import { useRef, useState } from 'react';
import { InputNumber } from 'antd';

import {
    getKpiRangeSpan,
    getUniformBinCountFromSize,
    hasKpiBinSizeError,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import {
    handleKpiIntegerInputKeyDown,
    KPI_INTEGER_INPUT_PROPS,
    KPI_RANGE_INPUT_CLASS,
    parseKpiIntegerInput,
} from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';
import { MIN_KPI_BIN_COUNT } from 'constants/ppa';
import { PPA_KPI_AXIS_BIN_COUNT, PPA_KPI_AXIS_BIN_SIZE } from 'constants/ppaTestIds';

const KPI_BIN_LINKED_FOCUS_CLASS = 'ant-input-number-focused';
const KPI_BIN_ERROR_INPUT_CLASS = '[&_.ant-input-number]:!border [&_.ant-input-number]:!border-redAlt';
const UNIFORM_BINNING_HELPER_TEXT = 'Size values may be auto adjusted to enforce uniform binning';

type KpiAxisConfigurationBinCountFieldProps = {
    min: number;
    max: number;
    binCount: number;
    binSize: number;
    countLabel: string;
    sizeLabel: string;
    onBinCountChange: (binCount: number) => void;
};

const KpiAxisConfigurationBinCountField = ({
    min,
    max,
    binCount,
    binSize,
    countLabel,
    sizeLabel,
    onBinCountChange,
}: KpiAxisConfigurationBinCountFieldProps) => {
    const rangeSpan = getKpiRangeSpan(min, max);
    const maxBinSize = Math.max(MIN_KPI_BIN_COUNT, rangeSpan);

    const [isGroupFocused, setIsGroupFocused] = useState(false);
    const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleGroupFocus = () => {
        if (blurTimerRef.current !== null) clearTimeout(blurTimerRef.current);
        setIsGroupFocused(true);
    };

    const handleGroupBlur = () => {
        blurTimerRef.current = setTimeout(() => setIsGroupFocused(false), 0);
    };

    const linkedFocusClass = isGroupFocused ? KPI_BIN_LINKED_FOCUS_CLASS : '';
    const hasBinSizeError = hasKpiBinSizeError(min, max, binCount);
    const binSizeErrorMessage = hasBinSizeError ? `Minimum required size is ${MIN_KPI_BIN_COUNT}` : null;

    return (
        <div className="flex flex-col gap-[12px]">
            <div className="flex gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-blackTransparent88">{countLabel}</span>
                        <InputNumber
                            size="small"
                            controls={false}
                            className={`${KPI_RANGE_INPUT_CLASS} ${linkedFocusClass}`}
                            value={binCount}
                            {...KPI_INTEGER_INPUT_PROPS}
                            onKeyDown={handleKpiIntegerInputKeyDown}
                            onFocus={handleGroupFocus}
                            onBlur={handleGroupBlur}
                            onChange={(value) => {
                                const parsed = parseKpiIntegerInput(value);
                                if (parsed == null) return;
                                onBinCountChange(Math.max(MIN_KPI_BIN_COUNT, parsed));
                            }}
                            data-testid={PPA_KPI_AXIS_BIN_COUNT}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-blackTransparent88">{sizeLabel}</span>
                        <InputNumber
                            size="small"
                            controls={false}
                            className={`${KPI_RANGE_INPUT_CLASS} ${linkedFocusClass} ${
                                hasBinSizeError ? KPI_BIN_ERROR_INPUT_CLASS : ''
                            }`}
                            min={MIN_KPI_BIN_COUNT}
                            max={maxBinSize}
                            value={binSize}
                            {...KPI_INTEGER_INPUT_PROPS}
                            onKeyDown={handleKpiIntegerInputKeyDown}
                            onFocus={handleGroupFocus}
                            onBlur={handleGroupBlur}
                            data-testid={PPA_KPI_AXIS_BIN_SIZE}
                            onChange={(value) => {
                                const parsed = parseKpiIntegerInput(value);
                                if (parsed == null) return;
                                const nextSize = Math.max(MIN_KPI_BIN_COUNT, Math.min(maxBinSize, parsed));
                                onBinCountChange(getUniformBinCountFromSize(min, max, nextSize));
                            }}
                        />
                    </div>
                </div>
            </div>
            <p className="break-words text-xs font-normal leading-4 text-toolbarLabelGrey">{UNIFORM_BINNING_HELPER_TEXT}</p>
            {binSizeErrorMessage ? <p className="mb-0 text-xs text-redAlt">{binSizeErrorMessage}</p> : null}
        </div>
    );
};

export default KpiAxisConfigurationBinCountField;
